/**
 * An entrance, and never a carrier.
 *
 * Everything on this page that enters the viewport does it through here, and the
 * component exists to make one mistake impossible: **text never animates from
 * invisible.** The offset is `transform` only. `opacity` is not animated at all,
 * because a block of prose sitting at `opacity: 0` — under reduced motion, with
 * JavaScript off, or in a crawler's first pass — is a block of prose that is not
 * on the page.
 *
 * Under reduced motion the element renders at its final position with no
 * timeline at all; `useReducedMotionSafe` answers on the first render, so there
 * is not even one animated frame to catch.
 */
import { m } from 'motion/react'
import type { ReactNode } from 'react'

import { useReducedMotionSafe } from './motion'

export function Reveal({
  children,
  y = 12,
  x = 0,
  delay = 0,
  duration = 0.32,
  className,
  as = 'div',
}: {
  children: ReactNode
  /** Vertical offset to enter from, in pixels. */
  y?: number
  /** Horizontal offset, for the rare figure that enters sideways. */
  x?: number
  delay?: number
  duration?: number
  className?: string
  as?: 'div' | 'section' | 'li' | 'p' | 'header'
}) {
  const reduced = useReducedMotionSafe()
  const Tag = m[as]

  if (reduced) {
    const Plain = as
    return <Plain className={className}>{children}</Plain>
  }

  return (
    <Tag
      className={className}
      initial={{ x, y }}
      whileInView={{ x: 0, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration, delay, ease: [0.32, 0.72, 0, 1] }}
    >
      {children}
    </Tag>
  )
}
